import { ReactElement, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@hopskipdrive/react-components';

import { ServerError, ServerErrorProps } from '../../auth/pages/ServerError';
import { useAuth } from '../../contexts/AuthContext';

import { axiosClient } from './provider';

interface AxiosErrorBoundaryProps {
  children: ReactElement;
}

export const AxiosErrorBoundary: React.FC<AxiosErrorBoundaryProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, handleSessionUnauthorized } = useAuth();
  const [serverError, setServerError] = useState<ServerErrorProps>();

  useEffect(() => {
    const responseInterceptor = axiosClient.interceptors.response.use(undefined, (error) => {
      if (error.response && error.response?.status) {
        const statusCode = error.response.status;

        switch (true) {
          case user && statusCode === 401:
            handleSessionUnauthorized();
            break;
          case statusCode === 403 || statusCode === 404:
            navigate(`${location.pathname}`, { state: { notFound: true } });
            break;
          case statusCode >= 500:
            setServerError({
              statusCode: statusCode,
              errorMessage: error.response.data && error.response.data?.errors[0],
            });
            break;
        }
      }

      throw error;
    });

    // To avoid the infinity loop if the latter also receives the response code 401, we eject the interceptor in this case
    return () => {
      axiosClient.interceptors.response.eject(responseInterceptor);
    };
  }, [user]);

  return serverError ? (
    <ThemeProvider>
      <ServerError statusCode={serverError.statusCode} errorMessage={serverError.errorMessage} />
    </ThemeProvider>
  ) : (
    children
  );
};
